import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Col, notification, Row } from 'antd';
import { CloseSquareOutlined, SaveFilled, SaveOutlined } from '@ant-design/icons';

import { InitialCard } from '@components';
import { CxSurveyQuestionProps, Survey } from '../Survey';
import { adminSettingsHttp, CxQuestionCategory, CxSurveyQuestionResponseProps } from '@network';
import cls from './CxAdminSurvey.module.css';

interface Props {
  readonly: boolean;
  maxLength?: number;
  category: CxQuestionCategory;
  title: string;
  hasAlterName?: boolean;
  hasMultiple?: boolean;
}

export const CxAdminSurvey = (
  { readonly, maxLength = 10, category, title, hasAlterName, hasMultiple = false }: Props,
) => {
  const [initialList, setInitialList] = useState<CxSurveyQuestionProps[]>([]);
  const [hasNewRow, setHasNewRow] = useState(false);
  const [questions, setQuestions] = useState<CxSurveyQuestionProps[]>([]);

  const isSaveDisabled = (): boolean => {
    if (hasNewRow) return true;
    if (JSON.stringify(initialList) === JSON.stringify(questions)) return true;
    if (questions.find(q => q.type === 'options' && q.options?.length === 0)) return true;

    return questions.some(question => question.isSelected);
  };

  const onSave = async (list: CxSurveyQuestionProps[]) => {
    try {
      await adminSettingsHttp.setSurvey(list, category);
      await getSurvey();
      notification.success({ message: 'Survey is saved!', placement: 'bottom' });
    } catch (e) {
      console.error('HTTP post err', e);
    }
  };

  const onDelete = () => {
    const filtered = questions
      .filter((i) => !i.isSelected)
      .sort((a, b) => a.position - b.position)
      .map((i, index) => ({ ...i, position: index + 1 }));
    setQuestions(filtered);
    onSave(filtered);
  };

  const transformQuestionsList = (
    item: CxSurveyQuestionResponseProps, idx: number,
  ): CxSurveyQuestionProps => {
    return {
      answer: item.answer || null,
      answeredByUser: item.answeredByUser,
      category: item.category,
      id: item.id || null,
      isEnabled: item.isEnabled,
      isRequired: item.isRequired,
      isSelected: false,
      name: item.name,
      nameClient: item.nameClient,
      options: item.options?.map((o, i) => (
        { ...o, idx: `${i}_${Date.now().toString()}` }),
      ) || null,
      position: item.position,
      type: item.type,
      idx: `${idx}_${Date.now().toString()}`,
    };
  };

  const getSurvey = async () => {
    try {
      const res = await adminSettingsHttp.getSurvey(category);
      if (res && Array.isArray(res)) {
        const list = res
          .map((i: CxSurveyQuestionResponseProps, idx: number) => transformQuestionsList(i, idx));
        setQuestions(list.map((i, idx) => ({ ...i, position: idx + 1 })));
        setInitialList(list);
      }
    } catch (e) {
      console.error('HTTP get err', e);
    }
  };

  useEffect(() => {
    (async () => {
      await getSurvey();
    })();
  }, []);

  return (
    <InitialCard
      left={<div>{title} <small>(max. {maxLength} questions)</small></div>}
      right={readonly ? undefined : (
        <div className={cls.buttons}>
          {questions.some(i => i.isSelected) ?
            <button className={clsx(cls.btn, cls.btnAlert)} onClick={onDelete}>
              <CloseSquareOutlined className={cls.btnIcon} />
            </button> : null}
          <button
            className={cls.btn} onClick={() => onSave(questions)}
            disabled={isSaveDisabled()}
          >
            {isSaveDisabled() ? (
              <SaveOutlined className={cls.btnIcon} />
            ) : (
              <SaveFilled className={cls.btnIcon} />
            )}
          </button>
        </div>
      )}
      extraPadding
      autoHeight
    >
      <Row>
        <Col xs={24}>
          <Survey
            category={category}
            hasAlterName={hasAlterName}
            list={questions}
            setList={setQuestions}
            hasNewRow={hasNewRow}
            setHasNewRow={setHasNewRow}
            maxLength={maxLength}
            hasMultiple={hasMultiple}
          />
        </Col>
      </Row>
    </InitialCard>
  );
};
